import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { prettyPrintFormula } from '@finley-technologies/calc-agent';
import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  FinancialCovenantResultGQLType,
  PortfolioCovenantResultResultGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  ExpandedCovenantChartData,
  expandedCovenantNonIntermediateFields,
  PortfolioChartFormat,
  valueTypeToFormatterMap,
} from '../shared/portfolioChartUtils';
import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { Icon } from 'packages/common-frontend/components/icons';
import { numberToZeroBelowThreshold } from 'packages/common-frontend/utils';
import {
  getThresholdLevel,
  isThresholdValueIntermediateField,
  ThresholdType,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';

const ResultBadge = ({
  result,
}: {
  result: PortfolioCovenantResultResultGQLType | FinancialCovenantResultGQLType;
}) => {
  return (
    <Flex
      alignItems="center"
      borderRadius="8px"
      backgroundColor={result === 'PASS' ? colors.green[100] : colors.red[100]}
      width="fit-content"
      padding="8px 16px"
    >
      <Icon
        icon="circle-check"
        color={result === 'PASS' ? colors.success : colors.error}
      />
      <Text
        ml="16px"
        fontWeight={500}
        color={result === 'PASS' ? colors.success : colors.error}
      >
        {commonUtils.toTitleCase(result)}
      </Text>
    </Flex>
  );
};

const CovenantChartDetailsGridRow = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: React.ReactElement;
}) => {
  return (
    <Grid templateColumns="repeat(5, 1fr)" margin="16px">
      <GridItem colSpan={2} padding="8px 0">
        <Flex alignItems="center">
          <Icon icon={icon} />
          <Text ml="16px" fontWeight={600}>
            {label}
          </Text>
        </Flex>
      </GridItem>
      <GridItem colSpan={3} pl="24px">
        {value}
      </GridItem>
    </Grid>
  );
};

const CovenantThresholdCard = ({
  label,
  value,
  leftIcon,
}: {
  label: string;
  value: Util.Primitive;
  leftIcon?: string;
}) => {
  return (
    <Box
      p="16px"
      borderColor={colors.gray[200]}
      borderWidth="1px"
      borderRadius="8px"
      width="400px"
      minWidth="400px"
    >
      <Text fontSize="14px" color={colors.black[70]} mb="8px">
        {label}
      </Text>
      <Flex alignItems="center">
        {leftIcon && <Icon icon={leftIcon} style={{ marginRight: '16px' }} />}
        <Text fontWeight={500}>{value}</Text>
      </Flex>
    </Box>
  );
};

const thresholdTypeToLabel: Record<ThresholdType, string> = {
  max: 'Maximum',
  min: 'Minimum',
};

const CovenantChartDetailsThresholdRow = ({
  intermediateFields,
  thresholdType,
  valueType,
}: {
  intermediateFields: {
    key: string;
    value: Util.Primitive;
  }[];
  thresholdType: ThresholdType | null;
  valueType: PortfolioChartFormat;
}) => {
  const thresholdIntermediateFields = intermediateFields.filter(
    (intermediateField) =>
      isThresholdValueIntermediateField(intermediateField.key),
  );
  const orderedThresholdIntermediateFields = _.orderBy(
    thresholdIntermediateFields,
    'key',
    'asc',
  );

  const formatter = valueTypeToFormatterMap[valueType];

  return (
    <Box m="16px">
      <Flex alignItems="center">
        <Icon icon="percent" />
        <Text ml="16px" fontWeight={600} py="8px">
          Threshold
        </Text>
      </Flex>
      <Flex overflow="auto" mt="16px" mb="24px" style={{ gap: '24px' }}>
        {thresholdType && (
          <CovenantThresholdCard
            label="Threshold Type"
            value={`${thresholdTypeToLabel[thresholdType]} Threshold`}
            leftIcon="gauge-simple-max"
          />
        )}
        {orderedThresholdIntermediateFields.map(
          (thresholdIntermediateField) => {
            const thresholdLevel = getThresholdLevel(
              thresholdIntermediateField.key,
            );

            return (
              <Tooltip
                label={thresholdIntermediateField.value}
                placement="bottom-start"
                key={thresholdIntermediateField.key}
              >
                <span>
                  <CovenantThresholdCard
                    label={`Level ${thresholdLevel}`}
                    value={formatter(thresholdIntermediateField.value)}
                  />
                </span>
              </Tooltip>
            );
          },
        )}
      </Flex>
    </Box>
  );
};

export const CovenantChartDetails = ({
  selectedDataPoint,
  thresholdType,
  covenantValueFormula,
  valueType,
}: {
  selectedDataPoint: ExpandedCovenantChartData;
  thresholdType: ThresholdType | null;
  covenantValueFormula: Util.Maybe<string>;
  valueType: PortfolioChartFormat;
}) => {
  const intermediateFields = _.compact(
    _.map(selectedDataPoint, (property, key) => {
      if (!_.includes(expandedCovenantNonIntermediateFields, key)) {
        return {
          key,
          value: property,
        };
      }
      return null;
    }),
  );

  const formatter = valueTypeToFormatterMap[valueType];

  return (
    <Box width="100%">
      <CovenantChartDetailsGridRow
        icon="box-ballot"
        label="Result"
        value={<ResultBadge result={selectedDataPoint.result} />}
      />
      <Divider />
      <CovenantChartDetailsGridRow
        icon="grip-lines"
        label="Value"
        value={
          <Tooltip label={selectedDataPoint.value} placement="bottom-start">
            <Text
              backgroundColor={colors.aurora[50]}
              padding="8px 16px"
              borderRadius="8px"
              fontWeight={500}
            >
              {_.isNumber(selectedDataPoint.value)
                ? formatter(
                    numberToZeroBelowThreshold({
                      num: selectedDataPoint.value,
                    }),
                  )
                : formatter(selectedDataPoint.value)}
            </Text>
          </Tooltip>
        }
      />
      <Divider />
      <CovenantChartDetailsThresholdRow
        intermediateFields={intermediateFields}
        thresholdType={thresholdType}
        valueType={valueType}
      />
      <Divider />
      <CovenantChartDetailsGridRow
        icon="calendar"
        label="Reporting Date"
        value={
          <Text fontWeight={500} padding="8px 0">
            {selectedDataPoint.reportingDate}
          </Text>
        }
      />
      <Divider />
      <Flex alignItems="center" margin="16px">
        <Icon icon="arrow-turn-down-right" />
        <Text ml="16px" fontWeight={600}>
          Intermediate Values
        </Text>
      </Flex>
      {covenantValueFormula && (
        <Box m="24px 16px" whiteSpace="pre-wrap">
          <Text fontWeight={500} mb="16px">
            Value Formula
          </Text>
          <FormulaContainer>
            <Text as="pre" whiteSpace="pre-wrap">
              {prettyPrintFormula(covenantValueFormula)}
            </Text>
          </FormulaContainer>
        </Box>
      )}
      <Box
        margin="16px"
        borderWidth="1px"
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            <Tr borderBottomWidth="2px">
              <Th
                w="40%"
                position="sticky"
                top="0"
                fontSize="16px"
                fontWeight={500}
                color={colors.black[95]}
              >
                Key
              </Th>
              <Th
                w="60%"
                position="sticky"
                top="0"
                fontSize="16px"
                fontWeight={500}
                color={colors.black[95]}
              >
                Value
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(intermediateFields, ({ key, value }) => {
              // hide intermediate fields that are required
              // hide threshold level fields
              // these are now separately highlighted already in other parts of this component
              if (
                !_.includes(
                  [
                    ...CreditAgreement.Config.PortfolioCovenant
                      .RequiredIntermediateFieldIterable,
                    ...CreditAgreement.Config.FinancialCovenant
                      .RequiredIntermediateFieldIterable,
                  ],
                  key,
                ) &&
                !isThresholdValueIntermediateField(key) &&
                !_.isNil(value) &&
                value !== ''
              ) {
                return (
                  <Tr
                    color={colors.black[70]}
                    key={`intermediate-field-row-${key}`}
                  >
                    <Td w="40%">{key}</Td>
                    <Td w="60%">
                      {_.isNumber(value)
                        ? numberToZeroBelowThreshold({
                            num: value,
                          })
                        : value}
                    </Td>
                  </Tr>
                );
              }

              return null;
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
